import { instance } from "libs/api";
import { DirectUploadMedia } from "libs/directUpload";
import isEmpty from "lodash/isEmpty";

export interface MoveElementInArrayProps<T> {
  array: T[];
  fromIndex: number;
  toIndex: number;
  keyForReorder?: keyof T;
}

export const moveElementInArray = <T>({
  array,
  fromIndex,
  toIndex,
  keyForReorder
}: MoveElementInArrayProps<T>) => {
  const copyArray = [...array];
  const [elementToMove] = copyArray.splice(fromIndex, 1);
  copyArray.splice(toIndex, 0, elementToMove);

  if (keyForReorder) {
    const startValue = array[0]?.[keyForReorder] as number;

    copyArray.forEach((item, index) => {
      item[keyForReorder] = (startValue + index) as T[keyof T];
    });
  }

  return copyArray;
};

export const replaceElementInArray = <T>(
  array: T[],
  data: T,
  index: number
) => {
  const copyArray = [...array];
  copyArray.splice(index, 1, data);

  return copyArray;
};

export const removeElementInArrayByIndex = <T>(array: T[], index: number) => {
  const copyArray = [...array];
  copyArray.splice(index, 1);

  return copyArray;
};

export const isEmptyValue = (value: string) => {
  return isEmpty(value.trim());
};

export const getVideoMetadata = async (
  file: File
): Promise<{ width: number; height: number; duration: number }> => {
  const video = document.createElement("video");
  video.src = URL.createObjectURL(file);

  return await new Promise((resolve, reject) => {
    video.onloadedmetadata = () => {
      resolve({
        width: video.videoWidth,
        height: video.videoHeight,
        duration: video.duration
      });
    };
    video.onerror = reject;
  });
};

export const firstLetterCapitalize = (word: string) => {
  return word?.[0].toUpperCase().concat(word.slice(1));
};

export const isLastIndexOfArray = (index: number, array: unknown[]) => {
  return index === array.length - 1;
};

export function toggleIndexInArray(
  prev: number | number[] | null,
  index: number
) {
  if (prev === null) return [index];
  if (!Array.isArray(prev)) return prev;

  return prev.includes(index)
    ? prev.filter((prevIndex) => prevIndex !== index)
    : [...prev, index];
}

export const onDownloadImage = async (image: DirectUploadMedia | null) => {
  if (!image) return;

  // Routed through our API (not the CDN) so the browser never hits the CDN's
  // missing CORS headers. instance adds auth + base URL; the response
  // interceptor returns the Blob body directly.
  const blob = await instance.get<Blob, Blob>(`/media/${image.id}/download`, {
    responseType: "blob"
  });

  const objectUrl = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = image.fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(objectUrl);
};

// Downloads the given media as a single zip built by the backend. The bytes are
// read from S3 server-side, so the browser never fetches the CDN directly —
// which is what made the old per-image fetch fail CORS. The API instance adds
// auth + base URL; its response interceptor returns the Blob body directly.
export const onDownloadMediaArchive = async (
  mediaList: DirectUploadMedia[],
  fileName = "images.zip"
) => {
  const mediaIds = mediaList.map((media) => media.id).filter(Boolean);
  if (!mediaIds.length) return;

  const blob = await instance.post<Blob, Blob>(
    "/media/archive",
    { mediaIds },
    { responseType: "blob" }
  );

  const objectUrl = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(objectUrl);
};

export const truncateText = (text: string, length: number) => {
  return text && text?.length > length
    ? `${text.substring(0, length)}...`
    : text;
};
